import { ComissaoEstatisticaAnaliticoResponse, ComissaoEstatisticaAgrupadaResponse } from '@/core/api/sicom-api/comissoes/estatisticas/comissao-estatistica.model';
import { UsuarioResumoResponse } from '@/core/api/sicom-api/sistema/usuario/usuario.model';
import { PendenciaLinha } from '../../gestao-administrativa/mapa-pendencias/pendencia-linha.model';

export interface RankingAssistente {
    posicao: number;
    codigo: string;
    idSolicitante: number | null;
    nome: string;
    total: number;
    percentual: number;
    avatar: string | null;
    iniciais: string;
}

function normalizar(texto: string): string {
    return (texto ?? '').normalize('NFD').replace(/\p{M}/gu, '').toUpperCase().trim();
}

export function iniciais(nome: string): string {
    return (
        (nome ?? '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((parte) => parte[0])
            .join('')
            .toUpperCase() || '?'
    );
}

export function excluirSemResponsavel(itens: ComissaoEstatisticaAgrupadaResponse[]): ComissaoEstatisticaAgrupadaResponse[] {
    return itens.filter((item) => {
        const total = Number(item.total) || 0;
        if (!item.codigo || total <= 0) {
            return false;
        }
        const descricao = normalizar(item.descricao ?? '');
        return descricao !== 'SEM ASSISTENTE' && descricao !== 'SEM COORDENADOR' && descricao !== 'NAO INFORMADO';
    });
}

export function resolverUsuario(item: ComissaoEstatisticaAgrupadaResponse, usuarios: UsuarioResumoResponse[]): UsuarioResumoResponse | null {
    const idSolicitante = Number(item.codigo);
    if (Number.isFinite(idSolicitante) && idSolicitante > 0) {
        const porId = usuarios.find((usuario) => usuario.idSolicitante === idSolicitante);
        if (porId) {
            return porId;
        }
    }
    const nome = normalizar(item.descricao ?? '');
    if (nome) {
        const porNome = usuarios.find((usuario) => normalizar(usuario.nmSolicitante ?? '') === nome);
        if (porNome) {
            return porNome;
        }
    }
    return null;
}

export function montarRanking(itens: ComissaoEstatisticaAgrupadaResponse[], usuarios: UsuarioResumoResponse[]): RankingAssistente[] {
    const ordenados = [...itens].sort((a, b) => (Number(b.total) || 0) - (Number(a.total) || 0));
    const maior = Number(ordenados[0]?.total) || 0;
    return ordenados.map((item, indice) => {
        const usuario = resolverUsuario(item, usuarios);
        const total = Number(item.total) || 0;
        const nome = usuario?.nmSolicitante || item.descricao || 'Assistente';
        const codigoNumerico = Number(item.codigo);
        return {
            posicao: indice + 1,
            codigo: item.codigo ?? '',
            idSolicitante: usuario?.idSolicitante ?? (Number.isFinite(codigoNumerico) ? codigoNumerico : null),
            nome,
            total,
            percentual: maior > 0 ? Math.round((total / maior) * 100) : 0,
            avatar: usuario?.avatar ?? null,
            iniciais: iniciais(nome)
        };
    });
}

export function formatarDataHoraReuniao(valor?: string | null): string {
    if (!valor) {
        return '-';
    }
    const [data, hora] = valor.split('T');
    const partes = (data ?? '').split('-');
    if (partes.length !== 3) {
        return '-';
    }
    const [ano, mes, dia] = partes;
    const horaMin = (hora ?? '').slice(0, 5);
    return horaMin ? `${dia}/${mes}/${ano} ${horaMin}` : `${dia}/${mes}/${ano}`;
}

export function mapLinhaReuniaoAnalitico(item: ComissaoEstatisticaAnaliticoResponse): PendenciaLinha {
    const camposExtras: { rotulo: string; valor: string }[] = [];

    const modalidade = (item.dsModalidade || '').trim();
    if (modalidade) {
        camposExtras.push({ rotulo: 'Modalidade', valor: modalidade });
    }

    const termino = formatarDataHoraReuniao(item.dhFimReuniao);
    if (termino && termino !== '-') {
        camposExtras.push({ rotulo: 'Término', valor: termino });
    }

    return {
        uf: item.grupoDescricao || item.grupoCodigo || '-',
        crm: item.idReuniao ? `#${item.idReuniao}` : '-',
        nome: item.dsEstruturaOrganizacional || 'Comissão',
        situacao: item.dsStatusReuniao || '-',
        codigoSituacao: String(item.idStatusReuniao ?? ''),
        detalhe: formatarDataHoraReuniao(item.dhInicioReuniao),
        ...(camposExtras.length ? { camposExtras } : {})
    };
}
